import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, useRxData, useRxQuery } from "rxdb-hooks";
import { RXDB_CLINIC_INSURANCE_COLLECTION_NAME } from "./clinic-insurance";
import { RXDB_CONSULTATION_COLLECTION_NAME } from "./consultation";
import { RXDB_DISPENSATION_COLLECTION_NAME } from "./dispensation";
import { RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME } from "./epocus-encounter";
import { RXDB_EPOCUS_EXAM_COLLECTION_NAME } from "./epocus-exam";
import { RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME } from "./epocus-probe-association";
import { RXDB_EPOCUS_PROGRAM_COLLECTION_NAME } from "./epocus-program";
import { RXDB_EPOCUS_REVIEW_COLLECTION_NAME } from "./epocus-review";
import { RXDB_EXCHANGE_RATE_COLLECTION_NAME } from "./exchange-rate";
import { RXDB_FACILITY_COLLECTION_NAME } from "./facility";
import { RXDB_IMAGING_INSTANCE_COLLECTION_NAME } from "./imaging-instance";
import { RXDB_IMAGING_STUDY_COLLECTION_NAME } from "./imaging-study";
import { RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME } from "./investigation-activity";
import { RXDB_INVOICE_COLLECTION_NAME } from "./invoice";
import { RXDB_PATIENT_COLLECTION_NAME } from "./patient";
import { RXDB_PRESCRIPTION_COLLECTION_NAME } from "./prescription";
import { RXDB_PRICE_COLLECTION_NAME } from "./price";
import { RXDB_PRICE_LIST_FACILITY_COLLECTION_NAME } from "./price-list-facility";
import { RXDB_PRICE_LIST_ITEM_COLLECTION_NAME } from "./price-list-item";
import { RXDB_PRICE_LIST_ITEM_EXTERNAL_REFERENCE_COLLECTION_NAME } from "./price-list-item-external-reference";
import { RXDB_PRICE_LIST_ITEM_NAME_COLLECTION_NAME } from "./price-list-item-name";
import { RXDB_PRICE_LIST_REVENUE_DEPARTMENT_COLLECTION_NAME } from "./price-list-revenue-department";
import { RXDB_PRICE_LIST_TARIFF_COLLECTION_NAME } from "./price-list-tariff";

export const RXDB_SYNC_CHECKPOINT_COLLECTION_NAME = "sync-checkpoint";

// Type-safe wrappers for all rxdb-hooks hooks. These should be copy-pasted for each
// collection, lest a type-wizard arrives.
// https://github.com/cvara/rxdb-hooks?tab=readme-ov-file#userxdocument
export const useRxCollectionSyncCheckpoint = () =>
  useRxCollection<SyncCheckpoint>(RXDB_SYNC_CHECKPOINT_COLLECTION_NAME);
export const useRxQuerySyncCheckpoint = (
  ...params: Parameters<typeof useRxQuery<SyncCheckpoint>>
) => useRxQuery<SyncCheckpoint>(...params);
type UseRxData = Parameters<typeof useRxData<SyncCheckpoint>>;
export const useRxDataSyncCheckpoint = (
  queryConstructor: UseRxData["1"],
  options?: UseRxData["2"],
) =>
  useRxData<SyncCheckpoint>(
    RXDB_SYNC_CHECKPOINT_COLLECTION_NAME,
    queryConstructor,
    options,
  );

const SyncCollections = [
  RXDB_PATIENT_COLLECTION_NAME,
  RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME,
  RXDB_PRESCRIPTION_COLLECTION_NAME,
  RXDB_DISPENSATION_COLLECTION_NAME,
  RXDB_CONSULTATION_COLLECTION_NAME,
  RXDB_IMAGING_INSTANCE_COLLECTION_NAME,
  RXDB_IMAGING_STUDY_COLLECTION_NAME,
  RXDB_FACILITY_COLLECTION_NAME,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
  RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME,
  RXDB_EPOCUS_PROGRAM_COLLECTION_NAME,
  RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
  RXDB_PRICE_LIST_FACILITY_COLLECTION_NAME,
  RXDB_PRICE_COLLECTION_NAME,
  RXDB_PRICE_LIST_TARIFF_COLLECTION_NAME,
  RXDB_PRICE_LIST_REVENUE_DEPARTMENT_COLLECTION_NAME,
  RXDB_PRICE_LIST_ITEM_COLLECTION_NAME,
  RXDB_PRICE_LIST_ITEM_NAME_COLLECTION_NAME,
  RXDB_PRICE_LIST_ITEM_EXTERNAL_REFERENCE_COLLECTION_NAME,
  RXDB_INVOICE_COLLECTION_NAME,
  RXDB_CLINIC_INSURANCE_COLLECTION_NAME,
  RXDB_EXCHANGE_RATE_COLLECTION_NAME,
];

const SYNC_CHECKPOINT_SCHEMA_LITERAL = {
  title: "sync-checkpoint schema",
  version: 17,
  description: "Stores RxDB checkpoints for an entityId/collection combo.",
  type: "object",
  // Composite primary key https://rxdb.info/rx-schema.html#composite-primary-key
  primaryKey: {
    key: "id",
    fields: ["entityId", "collectionName"],
    separator: "-",
  },
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    entityId: {
      type: "string",
      maxLength: 100,
    },
    collectionName: {
      type: "string",
      enum: SyncCollections,
      maxLength: 100,
    },
    checkpoint: {
      type: "object",
      properties: {
        id: {
          type: "string",
          maxLength: 100,
        },
        updatedAt: {
          type: "string",
          format: "date-time",
        },
      },
      required: ["id", "updatedAt"],
    },
  },
  required: ["id", "entityId", "collectionName", "checkpoint"],
  indexes: ["entityId", "collectionName"],
  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;
const schemaTyped = toTypedRxJsonSchema(SYNC_CHECKPOINT_SCHEMA_LITERAL);

// aggregate the document type from the schema
export type SyncCheckpoint = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

// create the typed RxJsonSchema from the literal typed object.
export const SYNC_CHECKPOINT_SCHEMA: RxJsonSchema<SyncCheckpoint> =
  SYNC_CHECKPOINT_SCHEMA_LITERAL;
